import {
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import { DashboardPage } from "@/pages/dashboard";
import { LandingPage } from "@/pages/landing";
import { isLoggedIn } from "@/lib/session";
import { GrowsListPage } from "@/pages/grows/list";
import { GrowDetailPage } from "@/pages/grows/detail";
import { GrowEditPage } from "@/pages/grows/edit";
import { GrowNewPage } from "@/pages/grows/new";
import { ReservoirsListPage } from "@/pages/reservoirs/list";
import { ReservoirDetailPage } from "@/pages/reservoirs/detail";
import { ReservoirNewPage } from "@/pages/reservoirs/new";
import { ReservoirEditPage } from "@/pages/reservoirs/edit";
import { PendingActionsPage } from "@/pages/pending-actions";
import { RefillsListPage } from "@/pages/refills/list";
import { RefillDetailPage } from "@/pages/refills/detail";
import { RefillNewPage } from "@/pages/refills/new";
import { RefillWizardPage } from "@/pages/refills/wizard";
import { ChemistryProfileLayout } from "@/pages/chemistry-profile/layout";
import { ChemistryWaterSourcesPage } from "@/pages/chemistry-profile/water-sources";
import { ChemistryNutrientsPage } from "@/pages/chemistry-profile/nutrients";
import { ChemistryRecipesPage } from "@/pages/chemistry-profile/recipes";
import { ChemistryCalibrationsPage } from "@/pages/chemistry-profile/calibrations";
import { WaterSourceNewPage } from "@/pages/water-sources/new";
import { WaterSourceDetailPage } from "@/pages/water-sources/detail";
import { WaterSourceEditPage } from "@/pages/water-sources/edit";
import { NutrientNewPage } from "@/pages/nutrients/new";
import { NutrientDetailPage } from "@/pages/nutrients/detail";
import { NutrientEditPage } from "@/pages/nutrients/edit";
import { RecipeNewPage } from "@/pages/recipes/new";
import { RecipeDetailPage } from "@/pages/recipes/detail";
import { RecipeBumpPage } from "@/pages/recipes/bump";
import { ChemistryPlantProfilesPage } from "@/pages/chemistry-profile/plant-profiles";
import { PlantProfileNewPage } from "@/pages/plant-profiles/new";
import { PlantProfileDetailPage } from "@/pages/plant-profiles/detail";
import { PlantProfileEditPage } from "@/pages/plant-profiles/edit";

import { RootLayout } from "./root";

// Route tree — TanStack Router code-based style. Bun's bundler doesn't
// ship a TanStack codegen integration yet (file-based discovery generates
// routeTree.gen.ts via Vite); code-based is the supported alternative and
// what's used here.

const rootRoute = createRootRoute({ component: RootLayout });

// Root "/" is the only route served to logged-out visitors (the server bounces
// every other path to auth). Render the public landing page when there's no
// session breadcrumb, the dashboard once signed in. RootLayout drops the app
// chrome for the logged-out case so the landing renders full-bleed.
function RootIndex() {
  return isLoggedIn() ? <DashboardPage /> : <LandingPage />;
}

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: RootIndex,
});

// Grows
const growsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grows",
  component: GrowsListPage,
});
const growNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grows/new",
  component: GrowNewPage,
});
const growDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grows/$growId",
  component: GrowDetailPage,
});
const growEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grows/$growId/edit",
  component: GrowEditPage,
});

// Reservoirs
const reservoirsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reservoirs",
  component: ReservoirsListPage,
});
const reservoirNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reservoirs/new",
  component: ReservoirNewPage,
});
const reservoirDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reservoirs/$reservoirId",
  component: ReservoirDetailPage,
});
const reservoirEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reservoirs/$reservoirId/edit",
  component: ReservoirEditPage,
});

// Pending actions
// `?grow=<id>` optionally pre-selects (filters to) one grow — e.g. arriving
// from a grow card's "next action". Optional, so the plain link defaults to
// all grows. Mirrors refillNewRoute's validateSearch.
const pendingActionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pending-actions",
  component: PendingActionsPage,
  validateSearch: (search: Record<string, unknown>): { grow?: string } => ({
    grow: typeof search.grow === "string" ? search.grow : undefined,
  }),
});

// Refills
const refillsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/refills",
  component: RefillsListPage,
});
// `?grow=<id>` optionally pre-selects the grow (e.g. starting a refill
// straight from a grow's pending action). Optional, so plain links still work.
const refillNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/refills/new",
  component: RefillNewPage,
  validateSearch: (search: Record<string, unknown>): { grow?: string } => ({
    grow: typeof search.grow === "string" ? search.grow : undefined,
  }),
});
const refillDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/refills/$sessionId",
  component: RefillDetailPage,
});
// The guided wizard owns a `?step=N` search param (1–8). validateSearch
// clamps anything out of range back to step 1 so a hand-edited URL can't
// land on an undefined step.
const refillWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/refills/$sessionId/wizard",
  component: RefillWizardPage,
  validateSearch: (search: Record<string, unknown>): { step: number } => {
    const n = Number(search.step);
    return { step: Number.isInteger(n) && n >= 1 && n <= 8 ? n : 1 };
  },
});

// Chemistry Profile (nested layout + sub-tabs)
const chemistryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chemistry-profile",
  component: ChemistryProfileLayout,
});
const chemistryWaterSourcesRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "/",
  component: ChemistryWaterSourcesPage,
});
const chemistryWaterSourceNewRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "water-sources/new",
  component: WaterSourceNewPage,
});
const chemistryWaterSourceDetailRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "water-sources/$waterSourceId",
  component: WaterSourceDetailPage,
});
const chemistryWaterSourceEditRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "water-sources/$waterSourceId/edit",
  component: WaterSourceEditPage,
});
const chemistryNutrientsRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "nutrients",
  component: ChemistryNutrientsPage,
});
// `?from=<id>` optionally pre-fills the form by cloning an existing nutrient
// (e.g. cloning a read-only system nutrient into an editable tenant copy).
const chemistryNutrientNewRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "nutrients/new",
  component: NutrientNewPage,
  validateSearch: (search: Record<string, unknown>): { from?: string } => ({
    from: typeof search.from === "string" ? search.from : undefined,
  }),
});
const chemistryNutrientDetailRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "nutrients/$nutrientId",
  component: NutrientDetailPage,
});
const chemistryNutrientEditRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "nutrients/$nutrientId/edit",
  component: NutrientEditPage,
});
const chemistryPlantProfilesRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "plant-profiles",
  component: ChemistryPlantProfilesPage,
});
// `?from=<id>` optionally pre-fills the form by cloning an existing profile.
const chemistryPlantProfileNewRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "plant-profiles/new",
  component: PlantProfileNewPage,
  validateSearch: (search: Record<string, unknown>): { from?: string } => ({
    from: typeof search.from === "string" ? search.from : undefined,
  }),
});
const chemistryPlantProfileDetailRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "plant-profiles/$plantProfileId",
  component: PlantProfileDetailPage,
});
const chemistryPlantProfileEditRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "plant-profiles/$plantProfileId/edit",
  component: PlantProfileEditPage,
});
const chemistryRecipesRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "recipes",
  component: ChemistryRecipesPage,
});
// `?from=<id>` optionally pre-fills the form by cloning an existing recipe.
const chemistryRecipeNewRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "recipes/new",
  component: RecipeNewPage,
  validateSearch: (search: Record<string, unknown>): { from?: string } => ({
    from: typeof search.from === "string" ? search.from : undefined,
  }),
});
const chemistryRecipeDetailRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "recipes/$recipeId",
  component: RecipeDetailPage,
});
const chemistryRecipeBumpRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "recipes/$recipeId/bump",
  component: RecipeBumpPage,
});
const chemistryCalibrationsRoute = createRoute({
  getParentRoute: () => chemistryRoute,
  path: "calibrations",
  component: ChemistryCalibrationsPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  growsListRoute,
  growNewRoute,
  growDetailRoute,
  growEditRoute,
  reservoirsListRoute,
  reservoirNewRoute,
  reservoirDetailRoute,
  reservoirEditRoute,
  pendingActionsRoute,
  refillsListRoute,
  refillNewRoute,
  refillDetailRoute,
  refillWizardRoute,
  chemistryRoute.addChildren([
    chemistryWaterSourcesRoute,
    chemistryWaterSourceNewRoute,
    chemistryWaterSourceDetailRoute,
    chemistryWaterSourceEditRoute,
    chemistryNutrientsRoute,
    chemistryNutrientNewRoute,
    chemistryNutrientDetailRoute,
    chemistryNutrientEditRoute,
    chemistryPlantProfilesRoute,
    chemistryPlantProfileNewRoute,
    chemistryPlantProfileDetailRoute,
    chemistryPlantProfileEditRoute,
    chemistryRecipesRoute,
    chemistryRecipeNewRoute,
    chemistryRecipeDetailRoute,
    chemistryRecipeBumpRoute,
    chemistryCalibrationsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
